import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';
import {
  Heading,
  H2,
  H3,
  Container,
  Text,
  Table,
  Tr,
  Th,
  Td
} from '../../components';

import { openDataQuery } from '../../core-types';
import { CodeBlock } from './CodeBlock';
import { InlineCode } from './InlineCode';
import { BlockContent } from '../../components/BlockContent';

const OpenDataWrapper = styled.div`
  max-width: 45em;
  margin-left: auto;
  margin-right: auto;
`;

const OverflowTable = styled.div`
  overflow-x: scroll;
  max-width: 100%;
`;

const messages = defineMessages({
  deepLinkTitle: {
    id: 'modules.OpenData.App.deepLinkTitle',
    defaultMessage: 'URI Schema'
  },
  deepLinkTitleAndroid: {
    id: 'modules.OpenData.App.deepLinkTitleAndroid',
    defaultMessage: 'Android'
  },
  deepLinkTitleIos: {
    id: 'modules.OpenData.App.deepLinkTitleIos',
    defaultMessage: 'iOS'
  },
  description: {
    id: 'description',
    defaultMessage: 'Description'
  },
  stationsDescription: {
    id: 'modules.OpenData.App.stationsDescription',
    defaultMessage: 'Opens the map view'
  },
  stationDescription: {
    id: 'modules.OpenData.App.stationDescription',
    defaultMessage:
      'Opens the map view with the station matching the station-id selected'
  },
  tripsDescription: {
    id: 'modules.OpenData.App.tripsDescription',
    defaultMessage: 'Opens the trip history view'
  },
  profileDescription: {
    id: 'modules.OpenData.App.profileDescription',
    defaultMessage: 'Opens the profile view'
  },
  commandLineExample: {
    id: 'modules.OpenData.App.commandLineExample',
    defaultMessage: 'Testing deep links from the command line on Android:'
  },
  intentExample: {
    id: 'modules.OpenData.App.intentExample',
    defaultMessage:
      'Testing deep links from your app code as an implicit intent on Android:'
  },
  quickUnlockDescription: {
    id: 'modules.OpenData.App.vehicleQuickUnlockDescription',
    defaultMessage: 'Opens a vehicle for quick unlock'
  },
  universalAppLinskTitle: {
    id: 'modules.OpenData.App.universalAppLinksTitle',
    defaultMessage: 'Universal/App Links'
  },
  universalAppLinskDescription: {
    id: 'modules.OpenData.App.universalAppLinksDescription',
    defaultMessage:
      'The deep links documented here are also supported as universal links. To use universal links, replace the deeplink prefix {deeplinkPrefix} with the universal link prefix instead:'
  },
  universalAppLinksExample: {
    id: 'modules.OpenData.App.universalAppLinksExample',
    defaultMessage:
      'For example, opening trips via universal link would be: {universalLinkExample}'
  }
});

const OpenData = ({ data }: { data: openDataQuery }) => {
  if (!data.openData || !data.system) {
    return null;
  }
  const { openData, system } = data;
  const { appDeepLinkingPrefix, publicDomain } = system;
  if (!appDeepLinkingPrefix) {
    return null;
  }
  return (
    <Container mt={6} mb={8}>
      <OpenDataWrapper>
        <Heading textAlign="center" mb={5}>
          {openData.appIntegrationTitle}
        </Heading>
        {openData.appIntegrationDescription && (
          <Text lead mb="md" textAlign="center">
            <BlockContent blocks={openData.appIntegrationDescription} />
          </Text>
        )}

        <H2>
          <FormattedMessage {...messages.deepLinkTitleAndroid} />
        </H2>
        <Text mb="sm">
          <FormattedMessage {...messages.deepLinkTitle} />
        </Text>
        <OverflowTable>
          <Table small>
            <thead>
              <Tr>
                <Th>URI</Th>
                <Th>
                  <FormattedMessage {...messages.description} />
                </Th>
              </Tr>
            </thead>
            <tbody>
              <Tr>
                <Td>
                  <InlineCode>{`${appDeepLinkingPrefix}://stations`}</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.stationsDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{`${appDeepLinkingPrefix}://stations/{station-id}`}</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.stationDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{`${appDeepLinkingPrefix}://trips`}</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.tripsDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{`${appDeepLinkingPrefix}://profile`}</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.profileDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>
                    {`${appDeepLinkingPrefix}://vehicle?code={code}`}
                  </InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.quickUnlockDescription} />
                </Td>
              </Tr>
            </tbody>
          </Table>
        </OverflowTable>

        <H3 mt="sm">
          <FormattedMessage {...messages.commandLineExample} />
        </H3>
        <CodeBlock>
          adb shell am start -W -a android.intent.action.VIEW -d{' '}
          {appDeepLinkingPrefix}
          ://trips
        </CodeBlock>

        <H3 mt="sm">
          <FormattedMessage {...messages.intentExample} />
        </H3>
        <CodeBlock>
          Intent tripsIntent = new Intent(Intent.ACTION_VIEW, Uri.parse(&quot;
          {appDeepLinkingPrefix}://trips&quot;));
          <br />
          this.startActivity(tripsIntent);
        </CodeBlock>
        <H2>
          <FormattedMessage {...messages.deepLinkTitleIos} />
        </H2>
        <Text mb="sm">
          <FormattedMessage {...messages.deepLinkTitle} />
        </Text>
        <OverflowTable>
          <Table small>
            <thead>
              <Tr>
                <Th>URI</Th>
                <Th>
                  <FormattedMessage {...messages.description} />
                </Th>
              </Tr>
            </thead>
            <tbody>
              <Tr>
                <Td>
                  <InlineCode>{appDeepLinkingPrefix}://stations</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.stationsDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{`${appDeepLinkingPrefix}://stations/{station-id}`}</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.stationDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{appDeepLinkingPrefix}://trips</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.tripsDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>{appDeepLinkingPrefix}://profile</InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.profileDescription} />
                </Td>
              </Tr>
              <Tr>
                <Td>
                  <InlineCode>
                    {`${appDeepLinkingPrefix}://vehicle?code={code}`}
                  </InlineCode>
                </Td>
                <Td>
                  <FormattedMessage {...messages.quickUnlockDescription} />
                </Td>
              </Tr>
            </tbody>
          </Table>
        </OverflowTable>
        <H3 mt="sm">
          <FormattedMessage {...messages.universalAppLinskTitle} />
        </H3>
        <Text mb={0}>
          <FormattedMessage
            {...messages.universalAppLinskDescription}
            values={{
              deeplinkPrefix: (
                <CodeBlock>{`${appDeepLinkingPrefix}://`}</CodeBlock>
              )
            }}
          />
        </Text>
        <CodeBlock>{`https://app.${publicDomain}`}</CodeBlock>
        <Text mb="sm">
          <FormattedMessage
            {...messages.universalAppLinksExample}
            values={{
              universalLinkExample: (
                <CodeBlock>{`https://app.${publicDomain}/trips`}</CodeBlock>
              )
            }}
          />
        </Text>
      </OpenDataWrapper>
    </Container>
  );
};

export default OpenData;
